import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { CheckOutlined, DownloadOutlined, LinkOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { App, Dropdown } from "antd5";
import { ItemType } from "antd5/es/menu/interface";

import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem } from "components/ui/dropdown_menu";
import Tooltip from "components/ui/tooltip/Tooltip";
import { copyToClipboard } from "lib/clipboard";
import { BidReport } from "lib/hooks/api/bid/bid_reports/useBidReport";
import { useExportBidReportAsPDF } from "lib/hooks/api/bid/bid_reports/useExportBidReportAsPDF";
import { useUpdateBidReportPublicity } from "lib/hooks/api/bid/bid_reports/useUpdateBidReportPublicity";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useSubscription } from "lib/hooks/subscription";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { usePaywall } from "lib/providers/Paywall";
import { routeSegments } from "lib/routes";
import { EventNames, useTracking } from "lib/tracking";
import { downloadFileFromLink, sanitizeFilename } from "lib/utils";
import { BidNoBidResponse } from "../../../lib/hooks/api/bid/answers/useGetBidReportAnswers";
import {
  BidWorkflowTransitionGroup,
  useBidWorkflow,
} from "../../../lib/hooks/api/bid/bids/useBidWorkflow";
import { useUpdateBids } from "../../../lib/hooks/api/bid/bids/useUpdateBids";
import { useFormAutoSubmit } from "../../../lib/hooks/useFormAutoSubmit";
import { Flex, Text } from "../../../styles/utility-components";
import { ExtendedStageOption, processTransitions } from "../../shared/stage/utils";
import { BidStageSelect } from "../../ui/form_components/StageSelect/BidStageSelect";
import { DROPDOWN_CLOSE_DELAY_MS } from "../common_report_components/utils";
import { exportBidReportAsCsv, exportBidReportAsDocx } from "./bidReportExportUtils";
import NoBidModal from "./NoBidModal";
import { DeleteBidReportModal, RegenerateAnswersModal } from "./ReportModals";

/**
 * Generates a partial SHA-256 hash (8 char length) from a random 32-byte array.
 * @returns
 */
async function generateRandomSHA256(): Promise<string> {
  // Generate a random 32-byte array (256 bits)
  const randomValues = new Uint8Array(32);
  window.crypto.getRandomValues(randomValues);

  // Hash the random data with SHA-256
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", randomValues);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");

  return hash.slice(0, 8);
}

export type BidNoBidHeaderActionButtonsProps = {
  bidReportId: string;
  bidInfo: BidReport;
  data: BidNoBidResponse | undefined;
  urlShareSignature?: string;
  slideoutOpen: boolean;
  setSlideoutOpen: (open: boolean) => void;
  onRegenerateBidReport: () => void;
};
export function BidNoBidHeaderActionButtons({
  bidReportId,
  bidInfo,
  data,
  urlShareSignature,
  slideoutOpen,
  setSlideoutOpen,
  onRegenerateBidReport,
}: BidNoBidHeaderActionButtonsProps) {
  const { teamId, notice, bid } = bidInfo;

  const { control, watch, getValues, trigger } = useForm<{ bidId?: string; stageId: string }>({
    defaultValues: {
      bidId: bid?.id,
      stageId: bid?.stage?.id ?? "",
    },
  });

  const { data: workflowData } = useBidWorkflow();

  const flatOptions = useMemo(() => {
    if (!workflowData?.transitions) return [];
    const grouped = processTransitions(workflowData.transitions);
    return grouped.flatMap((group) => group.options);
  }, [workflowData]);

  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const subscription = useSubscription();
  const hasBidStudio = subscription.hasDataTypes("BIDS");
  const { openPaywallModal } = usePaywall();

  const [shouldRedirectToRoot, setShouldRedirectToRoot] = useState(false);

  const { mutate: updateBids } = useUpdateBids({
    onSuccess: () => {
      message.success("Bid updated successfully");
      if (shouldRedirectToRoot) {
        void navigate({ to: "/bids" });
      }
    },
    onError: (error) => {
      console.warn("Failed to update bid", error);
      message.error("Failed to update bid");
      setShouldRedirectToRoot(false);
    },
  });

  const { logEvent } = useTracking();

  const { message } = App.useApp();

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isLinkCopied, setisLinkCopied] = useState(false);

  const [isNoBidModalOpen, setNoBidModalOpen] = useState(false);

  // Temporary holding state for bid & no bid flows when opp stage is changed
  const [nextStage, setNextStage] = useState<{ stageId: string; stageName: string } | null>(null);

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isRegenerateModalOpen, setRegenerateModalOpen] = useState(false);

  const [isCsvExporting, setIsCsvExporting] = useState(false);
  const [isDocxExporting, setIsDocxExporting] = useState(false);

  const { mutate: updateBidReportPublicity } = useUpdateBidReportPublicity();

  const { mutate: exportBidReportAsPDF, isLoading: isPdfExporting } = useExportBidReportAsPDF(
    bidReportId,
    {
      onSuccess: (url) => {
        if (!url) {
          message.error("Failed to export bid report as PDF");
          return;
        }

        const fileName = `Bid_no_bid_report${"_" + sanitizeFilename(bid?.name || "")}.pdf`;
        downloadFileFromLink(url, fileName);
        logEvent(EventNames.exportedData, {
          "Record GUID": notice?.guid,
          "Bid ID": bid?.id,
          "Bid report ID": bidReportId,
          "Action type": "Button clicked",
          "Data type": "Bid qualification (pdf)",
        });
      },
      onError: () => {
        message.error("Failed to export bid report as PDF");
      },
    },
  );

  const hasEditAccess = !!currentUser && currentUser.team.id === teamId;

  const handleCSVExport = useCallback(() => {
    setIsCsvExporting(true);
    if (data) {
      exportBidReportAsCsv(data, bid?.name);
      logEvent(EventNames.exportedData, {
        "Record GUID": notice?.guid,
        "Bid ID": bid?.id,
        "Bid report ID": bidReportId,
        "Action type": "Button clicked",
        "Data type": "Requirement summary report (csv)",
      });
    }
    setIsCsvExporting(false);
  }, [data, logEvent, notice?.guid, bid?.name, bid?.id, bidReportId]);

  const handleDocxExport = useCallback(async () => {
    if (!data) return;
    setIsDocxExporting(true);
    try {
      await exportBidReportAsDocx(data, bid?.name, notice);
      logEvent(EventNames.exportedData, {
        "Record GUID": notice?.guid,
        "Bid ID": bid?.id,
        "Bid report ID": bidReportId,
        "Action type": "Button clicked",
        "Data type": "Requirement summary report (docx)",
      });
    } catch (e) {
      Sentry.captureException(e);
      message.error("Failed to export requirement summary report as Word document");
    } finally {
      setIsDocxExporting(false);
    }
  }, [data, bid?.name, bid?.id, notice, logEvent, bidReportId, message]);

  async function copySignedUrl() {
    // TODO: Generate signature in the backend - assuming we are going to carry
    // on with this method of locking bid reports down
    // Generate the hash or get it from the URL or DB
    const signature = urlShareSignature ?? (await generateRandomSHA256());

    // Shared URL points to the dedicated public sharing route
    const shareUrl = `${window.location.origin}/shared/bid_reports/${bidReportId}?sig=${signature}`;

    const clipboardContent = {
      plain: shareUrl,
    };

    const copiedToClipboard = await copyToClipboard(clipboardContent);

    logEvent(EventNames.qualReportLinkCopied, {
      "Action type": "Link clicked",
    });

    if (!copiedToClipboard) {
      void message.error("Failed to copy link to clipboard", 2);
      return;
    }

    setisLinkCopied(true);

    // Only populate db with signature if it doesn't exist/hasn't happened already
    if (!urlShareSignature) {
      updateBidReportPublicity({ input: { bidReportId, publicHash: signature } });
    }
  }

  const updateBidStage = useCallback(
    (stageId: string, option?: ExtendedStageOption) => {
      if (!bid?.id) {
        return;
      }

      // If it's a no bid stage, open the no bid modal so they can
      // comment a reason for it being no bid
      if (option?.transitionGroup === BidWorkflowTransitionGroup.Disqualified) {
        setNextStage({ stageId, stageName: option.label });
        setNoBidModalOpen(true);
        return;
      }

      updateBids({
        updateBidsRequest: {
          ids: [bid.id],
          updateData: {
            stageId,
          },
        },
      });

      // If the user is changing the stage to bidding and don't have access to
      // bid studio, then show paywall
      if (!hasBidStudio && option?.transitionGroup === BidWorkflowTransitionGroup.In_progress) {
        openPaywallModal("CREATE_BIDS", { strategyPaywall: true });
      }
    },
    [bid?.id, updateBids, hasBidStudio, openPaywallModal],
  );

  const handleStageChange = useCallback(
    (values: { bidId?: string; stageId: string }, meta: { name: string }) => {
      if (meta.name !== "stageId") return;
      const option = flatOptions.find((o) => o.value === values.stageId);
      updateBidStage(values.stageId, option);
    },
    [flatOptions, updateBidStage],
  );

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: handleStageChange,
    provideMetadata: true,
  });

  const shareMenuItems: ItemType[] = [
    {
      key: "shareLink",
      label: isLinkCopied ? (
        <MenuItem primaryTextColor>
          <CheckOutlined />
          Link copied
        </MenuItem>
      ) : (
        <MenuItem onClick={async () => copySignedUrl()}>
          <LinkOutlined />
          Copy shareable link
        </MenuItem>
      ),
    },
    {
      key: "download-csv",
      label: (
        <MenuItem onClick={handleCSVExport}>
          {isCsvExporting ? <UIcon icon="spinner" /> : <DownloadOutlined />}
          Download CSV
        </MenuItem>
      ),
      disabled: isCsvExporting || !data || data.length == 0,
    },
    {
      key: "download-docx",
      label: (
        <MenuItem onClick={handleDocxExport}>
          <UIcon icon={isDocxExporting ? "spinner" : "fileDownload02"} />
          Download DOCX
        </MenuItem>
      ),
      disabled: isDocxExporting || !data?.length,
    },
    {
      key: "download-pdf",
      label: (
        <MenuItem onClick={() => exportBidReportAsPDF()}>
          <UIcon icon={isPdfExporting ? "spinner" : "fileDownload02"} />
          Download PDF
        </MenuItem>
      ),
      disabled: isPdfExporting || !data || data.length == 0,
    },
  ];

  return (
    <>
      <ActionButtonContainer readOnlyUser={!hasEditAccess} hasUser={!!currentUser}>
        {!hasEditAccess && (
          <Tooltip content="Log in to collaborate" side="bottom">
            <ViewOnlyText>View-only</ViewOnlyText>
          </Tooltip>
        )}
        <Tooltip content={slideoutOpen ? "Hide documents" : "View documents"} side="bottom">
          <Button
            as="button"
            onClick={() => {
              if (!slideoutOpen) {
                logEvent(EventNames.tenderDocumentsTrayOpened, {
                  "Bid ID": bid?.id,
                  "Action type": "Button clicked",
                });
              }
              setSlideoutOpen(!slideoutOpen);
            }}
            icon="folder"
            buttonStyle="inverse"
            iconProps={{ color: "sysPrimaryDefault" }}
          />
        </Tooltip>

        <Dropdown
          menu={{ items: shareMenuItems }}
          overlayStyle={{ minWidth: "170px" }}
          open={isDropdownOpen}
          disabled={!data || data.length == 0}
          mouseLeaveDelay={0.5}
          onOpenChange={(open) => {
            if (!open) {
              setTimeout(() => {
                setDropdownOpen(false);
                setisLinkCopied(false);
              }, DROPDOWN_CLOSE_DELAY_MS);
            } else {
              setDropdownOpen(open);
            }
          }}
        >
          <Button buttonType="secondary" disabled={!data || data.length == 0} icon="share07">
            <Text fontWeight="500">Share</Text>
            <UIcon icon="chevronDown" />
          </Button>
        </Dropdown>
        {hasEditAccess && (
          <>
            {bid?.id && (
              <BidStageForm>
                <BidStageSelect name="stageId" control={control} />
              </BidStageForm>
            )}

            <DropdownMenu
              trigger={
                <Button
                  buttonType="tertiary"
                  icon="dotsVertical"
                  iconProps={{ color: "sysTextDefault" }}
                />
              }
              width={175}
            >
              <DropdownMenuItem
                key="regenerate"
                icon="refreshCw03"
                onClick={() => setRegenerateModalOpen(true)}
              >
                Regenerate
              </DropdownMenuItem>
              <DropdownMenuItem
                key="manageCriteria"
                icon="settings01"
                disabled={!bid?.id}
                onClick={() => {
                  if (bid?.id) {
                    void navigate({
                      to: `/bids/${bid.id}/${routeSegments.requirementSummary}/${bidReportId}/bid-self-serve`,
                    });
                  }
                }}
              >
                Manage criteria
              </DropdownMenuItem>
              <DropdownMenuItem
                key="delete"
                icon="trash03"
                iconProps={{ color: "sysDestructiveDefault" }}
                onClick={() => setDeleteModalOpen(true)}
              >
                <Text color="sysDestructiveDefault">Delete</Text>
              </DropdownMenuItem>
            </DropdownMenu>

            <DeleteBidReportModal
              bidReportId={bidReportId}
              bidId={bid?.id}
              isOpen={isDeleteModalOpen}
              onClose={() => setDeleteModalOpen(false)}
            />
            <RegenerateAnswersModal
              isOpen={isRegenerateModalOpen}
              onRegenerate={onRegenerateBidReport}
              onClose={() => setRegenerateModalOpen(false)}
              modalText="You're about to regenerate all AI responses in this report. Please note, this action cannot be undone."
            />
          </>
        )}
      </ActionButtonContainer>

      {nextStage && bid?.id && (
        <NoBidModal
          open={isNoBidModalOpen}
          onOpenChange={(open) => setNoBidModalOpen(open)}
          bidId={bid.id}
          bidName={bid.name}
          stageName={nextStage.stageName.toLocaleLowerCase()}
          onConfirm={() => {
            updateBidStage(nextStage.stageId);
            setNextStage(null);
            setShouldRedirectToRoot(true);
          }}
        />
      )}
    </>
  );
}

const MenuItem = styled(Flex)<{ primaryTextColor?: boolean }>(({ theme, primaryTextColor }) => ({
  alignItems: "center",
  gap: "8px",
  color: primaryTextColor ? theme.colors.sysPrimaryDefault : theme.colors.sysTextDefault,
}));

const ActionButtonContainer = styled(Flex)<{ readOnlyUser: boolean; hasUser: boolean }>(
  ({ readOnlyUser, hasUser }) => ({
    alignItems: "center",
    gap: 8,
    // Fixing alignment of buttons depending on one of 3 outcomes.
    paddingRight: !readOnlyUser ? 0 : hasUser ? 24 : 8,
  }),
);

const ViewOnlyText = styled(Text)(({ theme }) => ({
  cursor: "pointer",
  color: theme.colors.sysPrimaryDefault,
  paddingRight: 24,
}));

const BidStageForm = styled.form({
  width: 200,
});
